// A phone in no family yet (WI-21, WI-37; mockups docs/mockups/family-start.html, owner-approved
// 2026-10-19): "Set it up" (just me, or me and my family) or "Join a family", and the recovery
// code for a family whose every phone is lost. Setting up asks for the parent's PIN from the
// start (D1) and shows the recovery code once; the app opens only after it has been saved.
import { useRef, useState, type ReactNode } from 'react'
import type { Person } from '../../shared/events'
import { COATS, hex, newChildFigure, ORGANISER_FIGURE } from '../../shared/figures'
import { MAX_CHILDREN, RECOVERY_LENGTH, type AgeBand } from '../../shared/family'
import { PinPad } from '../app/PinPad'
import { goTab, landOn } from '../app/router'
import { plausibleYear, setsUp } from '../domain/ageCheck'
import { Offline } from '../data/api'
import { authError, useSession, type StartFamily } from '../data/session'
import { t } from '../strings/en'

type Step = 'landing' | 'age' | 'young' | 'who' | 'you' | 'pin' | 'pin2' | 'kids' | 'code' | 'join' | 'recover'
interface Kid {
  key: number
  name: string
  ageBand: AgeBand
}

const AGES: AgeBand[] = ['u8', '8-12', '13+']
const w = t.welcome

export function Welcome() {
  const { pendingCode, startFamily, enter } = useSession()
  const [step, setStep] = useState<Step>(pendingCode ? 'join' : 'landing')
  const [solo, setSolo] = useState(false)
  const [family, setFamily] = useState('')
  const [name, setName] = useState('')
  const [coat, setCoat] = useState<number>(COATS[4])
  const [pin, setPin] = useState('')
  const [pinNote, setPinNote] = useState<string | null>(null)
  const [kids, setKids] = useState<Kid[]>([{ key: 0, name: '', ageBand: '8-12' }])
  const [started, setStarted] = useState<{ person: Person; recoveryCode: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const nextKey = useRef(1)

  const steps: Step[] = solo ? ['you', 'pin', 'code'] : ['you', 'pin', 'kids', 'code']
  const at = steps.indexOf(step === 'pin2' ? 'pin' : step)

  const submit = async (children: Kid[]) => {
    setBusy(true)
    setError(null)
    const req: StartFamily = {
      ...(solo ? { solo: true } : { family: family.trim() }),
      name: name.trim(),
      figure: { ...ORGANISER_FIGURE, coat, ...(solo ? { prop: 'camera' as const } : {}) },
      pin,
      ...(solo ? {} : { children: children.map((k, i) => ({ name: k.name.trim(), ageBand: k.ageBand, figure: newChildFigure(i) })) }),
    }
    try {
      setStarted(await startFamily(req))
      setStep('code')
    } catch (e) {
      setError(e instanceof Offline ? t.who.offline : w.failed)
    } finally {
      setBusy(false)
    }
  }

  if (step === 'landing') {
    return (
      <main className="screen who welcome">
        <ToyBox />
        <h1 className="h1 who-title">{w.title}</h1>
        <p className="muted welcome-sub">{w.sub}</p>
        <div className="welcome-choices">
          <button className="btn y press welcome-big" onClick={() => setStep('age')}>
            <span className="welcome-ic" aria-hidden="true">＋</span>
            <span>
              {w.setUp}
              <small>{w.setUpSub}</small>
            </span>
          </button>
          <button className="btn w press welcome-big" onClick={() => setStep('join')}>
            <span className="welcome-ic" aria-hidden="true">✉</span>
            <span>
              {w.join}
              <small>{w.joinSub}</small>
            </span>
          </button>
        </div>
        <button className="link welcome-link" onClick={() => setStep('recover')}>
          {w.lost}
        </button>
        <p className="info-links welcome-foot">
          <button className="link" onClick={() => goTab('install')}>
            {w.install}
          </button>
          <button className="link" onClick={() => goTab('privacy')}>
            {w.privacy}
          </button>
        </p>
      </main>
    )
  }

  // E10: the neutral age screen (mockups g01, g02). Asked every time; nothing is kept.
  if (step === 'age') return <AgeCheck onBack={() => setStep('landing')} onGrownUp={() => setStep('who')} onYounger={() => setStep('young')} />
  if (step === 'young') {
    return (
      <main className="screen who welcome">
        <Top label={w.setUp} onBack={() => setStep('landing')} />
        <ToyBox />
        <h1 className="h1 who-title">{w.youngTitle}</h1>
        <p className="muted welcome-sub">{w.youngSub}</p>
        <div className="welcome-choices">
          <button className="btn w press welcome-big" onClick={() => setStep('join')}>
            <span className="welcome-ic" aria-hidden="true">✉</span>
            <span>
              {w.join}
              <small>{w.joinSub}</small>
            </span>
          </button>
        </div>
      </main>
    )
  }

  if (step === 'who') {
    return (
      <Frame label={w.setUp} onBack={() => setStep('landing')} title={w.whoTitle} sub={w.whoSub}>
        <div className="welcome-choices">
          <button
            className="btn w press welcome-big tall"
            onClick={() => {
              setSolo(true)
              setStep('you')
            }}
          >
            <span className="welcome-ic blue" aria-hidden="true">1</span>
            <span>
              {w.solo}
              <small>{w.soloSub}</small>
            </span>
          </button>
          <button
            className="btn y press welcome-big tall"
            onClick={() => {
              setSolo(false)
              setStep('you')
            }}
          >
            <span className="welcome-ic" aria-hidden="true">4</span>
            <span>
              {w.family}
              <small>{w.familySub}</small>
            </span>
          </button>
        </div>
      </Frame>
    )
  }

  const label = solo ? w.stepSolo : w.stepFamily
  const dots = <Dots n={steps.length} at={at} />

  if (step === 'you') {
    const ok = name.trim() && (solo || family.trim())
    return (
      <Frame label={label} dots={dots} onBack={() => setStep('who')} title={solo ? w.youTitle : w.familyTitle} sub={solo ? w.youSub : w.familySubline}>
        <div className="welcome-form">
          {solo ? null : (
            <label className="field">
              <small>{w.familyName}</small>
              <input aria-label={w.familyName} value={family} maxLength={40} placeholder={w.familyNamePh} autoComplete="off" onChange={(e) => setFamily(e.target.value)} />
            </label>
          )}
          <label className="field">
            <small>{solo ? w.yourName : w.callYou}</small>
            <input aria-label={solo ? w.yourName : w.callYou} value={name} maxLength={24} placeholder={solo ? '' : w.callYouPh} autoComplete="off" onChange={(e) => setName(e.target.value)} />
          </label>
          <div>
            <p className="mono welcome-label">{w.coat}</p>
            <div className="welcome-coats" role="radiogroup" aria-label={w.coat}>
              {COATS.map((c, i) => (
                <button key={c} role="radio" aria-checked={c === coat} aria-label={w.coatName(i + 1)} className={`welcome-coat${c === coat ? ' on' : ''}`} style={{ background: hex(c) }} onClick={() => setCoat(c)} />
              ))}
            </div>
          </div>
        </div>
        <button className="btn y press welcome-next" disabled={!ok} onClick={() => setStep('pin')}>
          {w.next}
        </button>
      </Frame>
    )
  }

  if (step === 'pin' || step === 'pin2') {
    const again = step === 'pin2'
    return (
      <main className="screen who welcome">
        <Top label={label} dots={dots} onBack={() => setStep(again ? 'pin' : 'you')} />
        <PinPad
          key={step}
          title={again ? w.pinAgain : w.pinTitle}
          sub={again ? w.pinAgainSub : w.pinSub}
          note={again ? null : pinNote}
          onSubmit={async (p) => {
            if (!again) {
              setPin(p)
              setPinNote(null)
              setStep('pin2')
              return null
            }
            if (p !== pin) {
              setPinNote(w.pinMismatch)
              setStep('pin')
              return null
            }
            if (solo) await submit([])
            else setStep('kids')
            return null
          }}
        />
        {error ? <p className="who-error" role="alert">{error}</p> : null}
      </main>
    )
  }

  if (step === 'kids') {
    const named = kids.filter((k) => k.name.trim())
    return (
      <Frame label={label} dots={dots} onBack={() => setStep('pin')} title={w.kidsTitle} sub={w.kidsSub}>
        <div className="panel welcome-kids">
          {kids.map((k, i) => (
            <div key={k.key} className="welcome-kid">
              <label className="field">
                <small>{w.kidName}</small>
                <input
                  aria-label={`${w.kidName} ${i + 1}`}
                  value={k.name}
                  maxLength={24}
                  autoComplete="off"
                  onChange={(e) => setKids((all) => all.map((x) => (x.key === k.key ? { ...x, name: e.target.value } : x)))}
                />
              </label>
              <div className="seg" role="group" aria-label={k.name || w.kidName}>
                {AGES.map((a) => (
                  <button key={a} aria-pressed={k.ageBand === a} onClick={() => setKids((all) => all.map((x) => (x.key === k.key ? { ...x, ageBand: a } : x)))}>
                    {w.ages[a]}
                  </button>
                ))}
              </div>
              {kids.length > 1 ? (
                <button className="x-btn press welcome-remove" aria-label={w.removeKid(k.name)} onClick={() => setKids((all) => all.filter((x) => x.key !== k.key))}>
                  ✕
                </button>
              ) : null}
              {i < kids.length - 1 ? <hr /> : null}
            </div>
          ))}
        </div>
        {kids.length < MAX_CHILDREN ? (
          <button className="btn w press welcome-add" onClick={() => setKids((all) => [...all, { key: nextKey.current++, name: '', ageBand: '8-12' }])}>
            {w.addKid}
          </button>
        ) : null}
        {error ? <p className="who-error" role="alert">{error}</p> : null}
        <div className="welcome-next">
          <button className="btn y press" disabled={busy || !named.length} onClick={() => void submit(named)}>
            {w.next}
          </button>
          <button className="link" disabled={busy} onClick={() => void submit([])}>
            {w.kidsLater}
          </button>
        </div>
      </Frame>
    )
  }

  if (step === 'code' && started) return <RecoveryCode code={started.recoveryCode} label={label} dots={dots} onDone={() => {
          // The new parent lands on Organiser home and its set-up checklist (WI-19).
          landOn('organiser')
          void enter(started.person)
        }}
      />

  if (step === 'recover') return <Recover onBack={() => setStep('landing')} />

  return <Join onBack={() => setStep('landing')} />
}

/** The code, shown once. "Done" waits for the tick. */
function RecoveryCode({ code, label, dots, onDone }: { code: string; label: string; dots: ReactNode; onDone(): void }) {
  const [ticked, setTicked] = useState(false)
  const [copied, setCopied] = useState(false)
  return (
    <Frame label={label} dots={dots} title={w.codeTitle} sub={w.codeSub}>
      <p className="welcome-code" aria-label={code.split('').join(' ')}>
        {code}
      </p>
      <div className="welcome-row">
        <button
          className="btn w press"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(code)
              .then(() => setCopied(true))
              .catch(() => undefined)
          }}
        >
          {copied ? w.copied : w.copy}
        </button>
      </div>
      <label className="welcome-tick">
        <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
        <span>{w.saved}</span>
      </label>
      <button className="btn y press welcome-next" disabled={!ticked} onClick={onDone}>
        {w.done}
      </button>
    </Frame>
  )
}

/** Join with the parent's 6-letter code (or a /join# link, prefilled). */
function Join({ onBack }: { onBack(): void }) {
  const { pendingCode, link } = useSession()
  const [code, setCode] = useState((pendingCode ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 6))
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      await link(code)
    } catch (e) {
      setError(e instanceof Offline ? t.who.offline : t.join.badCode)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Frame label={t.welcome.join} onBack={onBack} title={t.join.title} sub={t.join.sub}>
      <p className="muted welcome-scan">{t.join.scan}</p>
      <p className="mono welcome-label center">{t.join.type}</p>
      <label className="welcome-slots">
        <input
          aria-label={t.phones.code}
          value={code}
          maxLength={6}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 6))}
        />
        {Array.from({ length: 6 }, (_, i) => (
          <i key={i} className={i === code.length ? 'cur' : ''} aria-hidden="true">
            {code[i] ?? ''}
          </i>
        ))}
      </label>
      <button className="btn y press welcome-wide" disabled={busy || code.length !== 6} onClick={() => void go()}>
        {t.join.go}
      </button>
      {error ? <p className="who-error" role="alert">{error}</p> : <p className="muted welcome-hint">{t.join.hint}</p>}
    </Frame>
  )
}

/** Every phone lost: the recovery code. */
function Recover({ onBack }: { onBack(): void }) {
  const recover = useSession((s) => s.recover)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const clean = code.toUpperCase().replace(/[^0-9A-Z]/g, '')
  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      await recover(clean)
    } catch (e) {
      setError(e instanceof Offline ? t.who.offline : authError(e)?.error === 'bad code' ? w.recoverBad : w.failed)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Frame label={w.recoverLabel} onBack={onBack} title={w.recoverTitle} sub={w.recoverSub}>
      <input
        className="join-code welcome-recover"
        aria-label={w.recoverTitle}
        value={code}
        maxLength={24}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        placeholder="XXXX-XXXX-XXXX-XXXX"
        onChange={(e) => setCode(e.target.value.toUpperCase())}
      />
      <button className="btn y press welcome-wide" disabled={busy || clean.length !== RECOVERY_LENGTH} onClick={() => void go()}>
        {w.recoverGo}
      </button>
      {error ? <p className="who-error" role="alert">{error}</p> : null}
    </Frame>
  )
}

const YEAR_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'] as const

/** "What year were you born?": four digits on a keypad, no default, then the setup or "ask a grown-up". */
function AgeCheck({ onBack, onGrownUp, onYounger }: { onBack(): void; onGrownUp(): void; onYounger(): void }) {
  const [year, setYear] = useState('')
  const full = year.length === 4
  const now = new Date()
  const wrong = full && !plausibleYear(Number(year), now)
  const press = (k: string) => setYear((y) => (k === '⌫' ? y.slice(0, -1) : (y + k).slice(0, 4)))
  return (
    <Frame label={w.setUp} onBack={onBack} title={w.ageTitle} sub={w.ageSub}>
      <div className="welcome-slots age-slots" role="status" aria-label={w.ageEntered(year)}>
        {[0, 1, 2, 3].map((i) => (
          <i key={i} className={i === year.length ? 'cur' : ''}>
            {year[i] ?? ''}
          </i>
        ))}
      </div>
      <p className="who-error" role="alert">
        {wrong ? w.ageWrong : ''}
      </p>
      <div className="pin-keys age-keys">
        {YEAR_KEYS.map((k, i) =>
          k ? (
            <button key={i} className="btn w press" aria-label={k === '⌫' ? t.pin.delete : k} onClick={() => press(k)}>
              {k}
            </button>
          ) : (
            <span key={i} />
          ),
        )}
      </div>
      <button className="btn y press welcome-next" disabled={!full || wrong} onClick={() => (setsUp(Number(year), now) ? onGrownUp() : onYounger())}>
        {w.next}
      </button>
    </Frame>
  )
}

function Frame({ label, dots, onBack, title, sub, children }: { label: string; dots?: ReactNode; onBack?(): void; title: string; sub: string; children: ReactNode }) {
  return (
    <main className="screen who welcome">
      <Top label={label} dots={dots} onBack={onBack} />
      <h1 className="h1 welcome-title">{title}</h1>
      <p className="muted welcome-sub">{sub}</p>
      {children}
    </main>
  )
}

function Top({ label, dots, onBack }: { label: string; dots?: ReactNode; onBack?(): void }) {
  return (
    <div className="welcome-top">
      {onBack ? (
        <button className="x-btn press" aria-label={w.back} onClick={onBack}>
          ‹
        </button>
      ) : null}
      <span className="mono">{label}</span>
      {dots}
    </div>
  )
}

function Dots({ n, at }: { n: number; at: number }) {
  return (
    <span className="welcome-dots" aria-label={`${at + 1} / ${n}`}>
      {Array.from({ length: n }, (_, i) => (
        <i key={i} className={i <= at ? 'on' : ''} />
      ))}
    </span>
  )
}

/** The landing's toy box: a crate, two capsules and a flag (placeholder art, CSS only). */
export function ToyBox() {
  return (
    <div className="welcome-toy" aria-hidden="true">
      <span className="crate" />
      <span className="cap c1" />
      <span className="cap c2" />
      <span className="flag" />
    </div>
  )
}
